'use client';

import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { UnauthQueryOptions } from '@/api';
import { DesktopSideAdBanner } from '@/app/_components/ads';
import { DesktopPageShell } from '@/components/template/DesktopPageShell';

import { PAGE_SIZE } from '../../constants';
import { buildPaginationMeta } from '../../utils';
import { SearchSectionShell } from '../SearchSectionShell';
import { GiftCardSkeletonList } from './GiftCardSkeletonList';
import { GiftFilter } from './GiftFilter';
import { GiftList } from './GiftList';

interface GiftSearchProps {
  keyword: string;
  page: number;
  onPageChange: (page: number) => void;
  isFilterOpen: boolean;
  onCloseFilter: () => void;
  categoryIds: number[];
  institutionIds: number[];
  onToggleCategory: (id: number) => void;
  onToggleInstitution: (id: number) => void;
  onReset: () => void;
}

export function GiftSearch({
  keyword,
  page,
  onPageChange,
  isFilterOpen,
  onCloseFilter,
  categoryIds,
  institutionIds,
  onToggleCategory,
  onToggleInstitution,
  onReset,
}: GiftSearchProps) {
  const { data, isLoading } = useQuery({
    ...UnauthQueryOptions['get:/wello/v2/allows/query/gift-products']({
      query: {
        searchKeyword: keyword,
        categoryIdList: categoryIds.length > 0 ? categoryIds : undefined,
        institutionIdList:
          institutionIds.length > 0 ? institutionIds : undefined,
        page,
        size: PAGE_SIZE,
      },
    }),
    placeholderData: keepPreviousData,
  });

  const meta = buildPaginationMeta(data?.context?.totalCount ?? 0);

  return (
    <DesktopPageShell
      className="mt-20 w-full mobile-tablet:mt-0"
      leftAside={
        <GiftFilter
          isDrawerOpen={isFilterOpen}
          onCloseDrawer={onCloseFilter}
          onApplyDrawer={onCloseFilter}
          categoryIds={categoryIds}
          institutionIds={institutionIds}
          onToggleCategory={onToggleCategory}
          onToggleInstitution={onToggleInstitution}
          onReset={onReset}
        />
      }
      rightAside={<DesktopSideAdBanner />}
    >
      <SearchSectionShell
        meta={meta}
        page={page}
        pageSize={PAGE_SIZE}
        onPageChange={onPageChange}
      >
        {isLoading ? (
          <GiftCardSkeletonList count={PAGE_SIZE} />
        ) : (
          <GiftList items={data?.context?.contents ?? []} />
        )}
      </SearchSectionShell>
    </DesktopPageShell>
  );
}
